import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useColumnOrder, useExpanded, useSortBy, useTable } from "react-table";

import Badge from "components/v2/Badge/Badge";
import Button from "components/v2/Button/Button";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import FormSection from "components/v2/FormSection/FormSection";
import { ReactComponent as Edit } from "assets/images/iconsV2/edit.svg";
import DataTable from "components/v2/Table/DataTable";
import { convertWeight, formatWeight } from "helpers/commonHelpers";
import usePermissions from "hooks/usePermissions";
import getSelectionOrderColumns from "./getSelectionOrderColumns";
import QuickRequestDrawer from "../../../QuickRequestDrawer/QuickRequestDrawer";

interface ISelectedOrdersProps {
  data: any;
  isLoading: boolean;
  setCurrentStep?: Function;
  isShipmentReview?: boolean;
}

function SelectedOrders({ data, isLoading, isShipmentReview, setCurrentStep }: ISelectedOrdersProps) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { unit_system_preferences } = JSON.parse(permissions ?? "{}");

  const [quickRequestReference, setQuickRequestReference] = useState<string | null>(null);

  const handleQRClick = useCallback((e: any, referenceNumber: string) => {
    e.stopPropagation();
    setQuickRequestReference(referenceNumber);
  }, []);

  const columns = useMemo(
    () => getSelectionOrderColumns(intl, isShipmentReview, handleQRClick, unit_system_preferences),
    [intl, isShipmentReview, handleQRClick, unit_system_preferences],
  );

  const instance = useTable(
    {
      columns,
      data,
      // initialState to set the last row as expanded.
      initialState: {
        expanded: {
          [data?.length - 1]: true,
        },
      },
    },
    useSortBy,
    useExpanded,
    useColumnOrder,
  );

  // Calculate totals for weight and volume
  const { totalWeight, totalVolume } = useMemo(() => {
    const goodItems = data.map((item: any) => item.data?.organization?.good_items || []).flat();
    return goodItems.reduce(
      (totals: { totalWeight: number; totalVolume: number }, item: any) => {
        const toUnit = unit_system_preferences === "inch-lbs" ? "lbs" : "kg";
        const itemUnit = item.w_unit === "Lbs" ? "lbs" : "kg";
        const itemWeight = (item?.weight || 0) * (item.units || 1);
        const convertedWeight = parseFloat(convertWeight(itemWeight, { fromUnit: itemUnit, toUnit }));

        const volume = parseFloat(item.total_cbm?.toFixed(2) ?? 0);

        return {
          totalWeight: totals.totalWeight + convertedWeight,
          totalVolume: totals.totalVolume + volume,
        };
      },
      { totalWeight: 0, totalVolume: 0 },
    );
  }, [data, unit_system_preferences]);

  // Define the RowSubComponent for rendering below the last row
  function RowSubComponent() {
    return (
      <div className="d-flex justify-content-between sub-list">
        <div className="text-sm font-semibold sub-list-spacing">
          <FormattedMessage id="general.total" defaultMessage="Total" />
        </div>
        <div className="d-flex">
          <div
            className={`d-flex justify-content-end text-sm font-semibold sub-list-spacing ${
              isShipmentReview ? "review" : ""
            }`}
          >
            {formatWeight(totalWeight.toFixed(2))}
          </div>
          <div
            className={`d-flex justify-content-end text-sm font-semibold sub-list-spacing ${
              isShipmentReview ? "review" : ""
            }`}
          >
            {formatWeight(totalVolume.toFixed(2))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3xl">
      <QuickRequestDrawer
        isOpen={quickRequestReference}
        handleClose={() => setQuickRequestReference(null)}
        QuickRequestReference={quickRequestReference}
      />
      <FormSection>
        <FormHeader
          title="Selected orders"
          badge={<Badge label={`${data?.length} orders`} color="gray" type="category-badge" />}
          button={
            !isShipmentReview && (
              <Button
                variant="link-color"
                iconLeft={<Edit />}
                label="Edit Selected Order"
                onClick={() => setCurrentStep!(1)}
              />
            )
          }
        />
        <div className="form-section-content">
          <DataTable tableInstance={instance} isLoading={isLoading} RowSubComponent={RowSubComponent} />
        </div>
      </FormSection>
    </div>
  );
}

SelectedOrders.defaultProps = {
  isShipmentReview: false,
  setCurrentStep: undefined,
};

export default SelectedOrders;
